import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// This is a backup of the simplified version - to be deleted after verification
// The full working version with all functionality is being restored
import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useLocale } from '../../locale/LocaleProvider';
import { useNavigate } from 'react-router-dom';
// search/navigation handled in NavBar
import { resolvePublicUrl } from '../../utils/publicUrl';
import { applyWvLandingDepartmentHeroImages, mergeDepartmentImagesFromPageJson, } from '../../services/landingDepartmentMerge';
import { getWvBrandingSafe } from '../../services/wvBrandingConfig';
import NavBar from '../CMS/NavBar/NavBar';
import { cmsPortalTypography } from '../../theme/cmsTypography';
import { WV_GOLD } from '../../theme';
import { portalContentMaxWidthSx, portalPageHorizontalPaddingSx } from '../../theme/portalLayout';
import { fetchLandingGrids } from '../../store/landingGridsSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { LandingPortraitCarousel } from './LandingPortraitCarousel';
import { LandingBrowseBand } from './LandingBrowseBand';
import * as OneStopFullHeroModule from './OneStopFullHero';
const OneStopFullHero = OneStopFullHeroModule.default || OneStopFullHeroModule;
import SpeedToBuildHero from './SpeedToBuildHero';
/**
 * Client "Speed to Build" hero redesign. Flip to `false` to instantly revert to the previous
 * {@link OneStopFullHero} (which is left untouched) — no other change needed.
 */
const USE_SPEED_TO_BUILD_HERO = false;
const HeroComponent = USE_SPEED_TO_BUILD_HERO ? SpeedToBuildHero : OneStopFullHero;
import OneStopAgenciesRow from './OneStopAgenciesRow';
import SpeedToBuildCtaBand from './SpeedToBuildCtaBand';
import SystemBannerFromGrid from './SystemBannerFromGrid';
import ConstructionIcon from '@mui/icons-material/Construction';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import BusinessIcon from '@mui/icons-material/Business';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ForestIcon from '@mui/icons-material/Forest';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
const LandingHero = ({ title, titleLines, titleImage, titleImageAlt, subheading, subheadingLines, description, descriptionLines, backgroundImage, heroBackgroundPosition = 'center center', heroBackgroundSize = 'cover', heroBackgroundAspectRatio, heroBackgroundHeightScale, nav, searchPlaceholder = 'Search ...', searchButtonLabel = 'Search', searchActionUrl, agenciesHeading = 'Agencies', agenciesSubheading, agenciesCarouselIntervalMs = 9000, agenciesBackgroundImage, agenciesBackgroundPosition, agenciesBackgroundSize, departmentsHeading = 'Departments', departmentsSubheading, departments: departmentImageOverridesFromPageJson = [], features = [], heroSpacing, systemBanner, }) => {
    const theme = useTheme();
    const ll = theme.landingHeroLayout;
    const { locale, shell } = useLocale();
    const cms = useMemo(() => cmsPortalTypography(theme), [theme]);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const landingGrids = useAppSelector((s) => s.landingGrids);
    const resolvedDepartments = useMemo(() => applyWvLandingDepartmentHeroImages(mergeDepartmentImagesFromPageJson(landingGrids.departmentsRaw, departmentImageOverridesFromPageJson)), [landingGrids.departmentsRaw, departmentImageOverridesFromPageJson]);
    const landingAgencies = landingGrids.agencies;
    const gridsBusy = landingGrids.status === 'idle' || landingGrids.status === 'loading';
    const departmentsLoading = gridsBusy;
    const agenciesLoading = gridsBusy;
    const departmentsLoadError = landingGrids.departmentsLoadError;
    const agenciesLoadError = landingGrids.agenciesLoadError;
    /** One env read + parallel department + agency grids (see `landingGridsSlice`). */
    useEffect(() => {
        void dispatch(fetchLandingGrids());
    }, [dispatch]);
    const assistantRequests = [
        { icon: ConstructionIcon, label: 'Build a deck' },
        { icon: LocalShippingIcon, label: 'Start a food truck' },
        { icon: RestaurantIcon, label: 'Open a restaurant' },
        { icon: BusinessIcon, label: 'Register a business' },
        { icon: WaterDropIcon, label: 'Install a septic system' },
        { icon: ForestIcon, label: 'Timber harvesting' },
    ];
    const assistantBenefits = [
        {
            icon: LightbulbOutlinedIcon,
            title: 'AI-Powered Permit Guidance',
            text: 'Get personalized guidance, required permits, estimated timelines, fees, and step-by-step instructions.',
        },
        {
            icon: VerifiedUserIcon,
            title: 'Smart Recommendations',
            text: 'AI identifies the right permits for your project.',
        },
        {
            icon: AccessTimeIcon,
            title: 'Save Time',
            text: 'Get to the right application faster.',
        },
        {
            icon: AutoAwesomeIcon,
            title: 'Accurate & Current',
            text: 'Always up-to-date requirements.',
        },
        {
            icon: LockOutlinedIcon,
            title: 'Secure & Private',
            text: 'Your information is protected.',
        },
    ];
    const agenciesMarginBottom = useMemo(() => ({
        xs: heroSpacing?.agenciesMarginBottom?.xs ?? ll.agencies.marginBottomDefault.xs,
        md: heroSpacing?.agenciesMarginBottom?.md ?? ll.agencies.marginBottomDefault.md,
    }), [heroSpacing?.agenciesMarginBottom, ll.agencies.marginBottomDefault]);
    const effectiveBackgroundImage = backgroundImage ?? getWvBrandingSafe().wvHeroCapitolDomeJpg;
    const heroBackgroundResolved = resolvePublicUrl(effectiveBackgroundImage) ?? effectiveBackgroundImage;
    /**
     * When the hero photo is configured as `100% auto`, reserve enough vertical space to show
     * more of the image’s intrinsic height instead of aggressively cropping it to content height.
     */
    const heroPhotoMinHeight = useMemo(() => {
        if (!/^\s*100%\s+auto\s*$/i.test(heroBackgroundSize ?? '')) {
            return ll.hero.minHeight;
        }
        const ratioRaw = Number(heroBackgroundAspectRatio);
        const ratio = Number.isFinite(ratioRaw) && ratioRaw > 0 ? ratioRaw : 1.333;
        const scaleRaw = Number(heroBackgroundHeightScale);
        const scale = Number.isFinite(scaleRaw) && scaleRaw > 0 ? scaleRaw : 1;
        return {
            xs: `max(23rem, calc((100vw / ${ratio}) * ${scale}))`,
            sm: `max(24rem, calc((100vw / ${ratio}) * ${scale}))`,
            md: `max(25rem, calc((100vw / ${ratio}) * ${scale}))`,
            lg: `max(26rem, calc((100vw / ${ratio}) * ${scale}))`,
        };
    }, [heroBackgroundSize, heroBackgroundAspectRatio, heroBackgroundHeightScale, ll.hero.minHeight]);
    const effectiveAgenciesBackgroundImage = agenciesBackgroundImage ?? backgroundImage ?? getWvBrandingSafe().wvHeroCapitolDomeJpg;
    const agenciesBackgroundResolved = resolvePublicUrl(effectiveAgenciesBackgroundImage) ?? effectiveAgenciesBackgroundImage;
    const agenciesBgPosition = agenciesBackgroundPosition ?? heroBackgroundPosition;
    const agenciesBgSize = agenciesBackgroundSize ?? heroBackgroundSize;
    /**
     * The hero already paints this photo full-bleed behind the column. A second layer on the agencies
     * panel uses `background-size: cover` relative to that smaller box, so the image rescales and looks
     * like a harsh “break” at the seam. Skip the duplicate when URL + position + size match the hero.
     */
    const agenciesShareHeroBackdrop = useMemo(() => agenciesBackgroundResolved === heroBackgroundResolved &&
        agenciesBgPosition === heroBackgroundPosition &&
        agenciesBgSize === heroBackgroundSize, [
        agenciesBackgroundResolved,
        heroBackgroundResolved,
        agenciesBgPosition,
        heroBackgroundPosition,
        agenciesBgSize,
        heroBackgroundSize,
    ]);
    // Detect One Stop Shop design based on branding graphic or title text
    const oneStopGraphic = getWvBrandingSafe().oneStopShopTitleGraphicPng;
    const resolvedTitleImage = resolvePublicUrl(titleImage ?? '') ?? (titleImage ?? '');
    const oneStopGraphicUrl = resolvePublicUrl(oneStopGraphic) ?? oneStopGraphic;
    const showOneStop = (!!oneStopGraphicUrl && !!resolvedTitleImage && resolvedTitleImage === oneStopGraphicUrl) ||
        (!!title && title.toLowerCase().includes('one-stop shop'));
    // dynamic offset to ensure agencies/cards start below the hero background (dome) line
    const [oneStopAgenciesOffset, setOneStopAgenciesOffset] = useState(null);
    useEffect(() => {
        if (!showOneStop) {
            setOneStopAgenciesOffset(null);
            return;
        }
        const measure = () => {
            const heroEl = document.getElementById('hero');
            const bgEl = document.getElementById('hero-bg');
            if (!heroEl || !bgEl) {
                setOneStopAgenciesOffset(null);
                return;
            }
            const heroRect = heroEl.getBoundingClientRect();
            const bgRect = bgEl.getBoundingClientRect();
            const delta = Math.round(bgRect.bottom - heroRect.bottom);
            setOneStopAgenciesOffset(delta > 0 ? delta : 0);
        };
        measure();
        const t = setTimeout(measure, 250);
        window.addEventListener('resize', measure);
        return () => {
            window.removeEventListener('resize', measure);
            clearTimeout(t);
        };
    }, [showOneStop, landingAgencies]);
    if (showOneStop) {
        return (_jsxs(_Fragment, { children: [_jsx(HeroComponent, { title: title, titleLines: titleLines || undefined, titleImage: titleImage, titleImageAlt: titleImageAlt, subheading: subheading, subheadingLines: subheadingLines || undefined, description: description, descriptionLines: descriptionLines || undefined, backgroundImage: backgroundImage, nav: nav, systemBanner: systemBanner, agencies: landingAgencies, resolvedDepartments: resolvedDepartments, loading: agenciesLoading, loadError: agenciesLoadError, agenciesCarouselIntervalMs: agenciesCarouselIntervalMs, agenciesBackgroundResolved: agenciesBackgroundResolved, agenciesBgPosition: agenciesBgPosition, agenciesBgSize: agenciesBgSize }), _jsx(Box, { id: "agencies", sx: { width: '100%', bgcolor: 'transparent', scrollMarginTop: 88 }, style: { marginTop: oneStopAgenciesOffset !== null ? `${oneStopAgenciesOffset}px` : undefined }, children: _jsx(OneStopAgenciesRow, { gridRows: landingAgencies, resolvedDepartments: resolvedDepartments, loading: agenciesLoading, loadError: agenciesLoadError }) }), _jsx(SpeedToBuildCtaBand, {}), _jsx(LandingBrowseBand, { tiles: resolvedDepartments, loading: departmentsLoading, loadError: departmentsLoadError, heading: departmentsHeading, subheading: departmentsSubheading, bandMarginBottom: agenciesMarginBottom, trustFeaturesPresent: false })] }));
    }
    const autoReadableColor = '#032A3A';
    // Search behavior moved to NavBar; hero no longer renders the search form.
    /** Left-biased scrim with a soft green tint for headline/search readability while keeping the image sharp. */
    const overlay = `linear-gradient(105deg, ${alpha('#dff3e4', 0.66)} 0%, ${alpha('#eaf7ee', 0.4)} 24%, ${alpha('#d0ebd8', 0.16)} 44%, ${alpha(theme.palette.primary.dark, 0.14)} 100%)`;
    /** Softens the edge where the photo meets the solid white strip (trust bar straddles this seam). */
    const heroBottomFade = `linear-gradient(to bottom, transparent 0%, ${alpha('#fff', 0.06)} 42%, ${alpha('#fff', 0.32)} 100%)`;
    return (_jsxs(_Fragment, { children: [_jsx(Box, { id: "hero", component: "section", sx: {
                    bgcolor: '#e8f4ea',
                    scrollMarginTop: 16,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    overflowX: 'clip',
                    /** Later DOM siblings (departments band) must not paint over the trust card overlap */
                    position: 'relative',
                    zIndex: 2,
                }, children: _jsxs(Box, { sx: {
                        position: 'relative',
                        overflowX: 'clip',
                        overflowY: 'visible',
                        minHeight: heroPhotoMinHeight,
                        ...ll.hero.wideShellSx,
                        pt: ll.hero.paddingTop,
                        pb: {
                            xs: features.length > 0
                                ? ll.hero.paddingBottomWithTrustFeatures.xs
                                : ll.hero.paddingBottomNoTrustFeatures.xs,
                            md: features.length > 0
                                ? ll.hero.paddingBottomWithTrustFeatures.md
                                : ll.hero.paddingBottomNoTrustFeatures.md,
                            lg: features.length > 0
                                ? ll.hero.paddingBottomWithTrustFeatures.lg
                                : ll.hero.paddingBottomNoTrustFeatures.lg,
                        },
                        bgcolor: '#e8f4ea',
                    }, children: [_jsx(Box, { "aria-hidden": true, sx: {
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 0,
                                bgcolor: '#e8f4ea',
                                backgroundImage: `url(${heroBackgroundResolved})`,
                                /** `cover` + full-bleed layer avoids letterboxing gutters on ultrawide viewports */
                                backgroundSize: heroBackgroundSize ?? 'cover',
                                backgroundPosition: heroBackgroundPosition,
                                backgroundRepeat: 'no-repeat',
                            } }), _jsx(Box, { "aria-hidden": true, sx: {
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1,
                                background: overlay,
                                pointerEvents: 'none',
                            } }), features.length === 0 ? (_jsx(Box, { "aria-hidden": true, sx: {
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 2,
                                height: ll.hero.bottomFadeHeight,
                                background: heroBottomFade,
                                pointerEvents: 'none',
                            } })) : null, _jsxs(Box, { sx: {
                                position: 'relative',
                                zIndex: 3,
                                width: '100%',
                                minWidth: 0,
                                ...portalPageHorizontalPaddingSx,
                            }, children: [nav && (_jsx(Box, { id: "navbar", sx: { width: '100%' }, children: _jsx(NavBar, { ...nav, embedInHero: true, searchPlaceholder: searchPlaceholder, searchButtonLabel: searchButtonLabel, searchActionUrl: searchActionUrl }) })), systemBanner ? _jsx(SystemBannerFromGrid, { ...systemBanner }) : null, _jsx(Box, { sx: {
                                        ...portalContentMaxWidthSx,
                                        mt: nav ? { xs: 0.35, md: 0.65 } : 0,
                                        /** Lets nested agency strip shrink & scroll instead of clipping in flex layout */
                                        minWidth: 0,
                                    }, children: _jsxs(Box, { sx: {
                                            width: '100%',
                                            minWidth: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                        }, children: [_jsx(Box, { sx: {
                                                    width: '100%',
                                                    minWidth: 0,
                                                    display: 'flex',
                                                    flexDirection: { xs: 'column', lg: 'row' },
                                                    alignItems: { xs: 'stretch', lg: 'flex-start', xl: 'center' },
                                                    justifyContent: { xs: 'stretch', lg: 'flex-start' },
                                                    gap: ll.headlineRow.gap,
                                                    mb: ll.headlineRow.marginBottom,
                                                }, children: _jsxs(Box, { sx: {
                                                        flex: { xs: '1 1 auto', lg: '0 1 auto' },
                                                        minWidth: 0,
                                                        maxWidth: { lg: 'min(100%, 720px)', xl: 'min(100%, 860px)' },
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-start',
                                                        textAlign: 'left',
                                                    }, children: [showOneStop ? (_jsxs(Box, { component: "div", sx: { display: 'flex', alignItems: 'center', gap: 3, width: '100%' }, children: [_jsx(Box, { component: "img", src: resolvePublicUrl(titleImage) ?? titleImage, alt: titleImageAlt ?? title, sx: { height: { xs: 72, md: 120 }, width: 'auto', display: 'block' } }), _jsxs(Box, { sx: { flex: 1, minWidth: 0, textAlign: { xs: 'center', lg: 'left' } }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', lg: 'flex-start' }, gap: 2, mb: 1 }, children: [_jsx(Box, { sx: { height: 4, width: 64, bgcolor: WV_GOLD, borderRadius: 1, display: { xs: 'none', md: 'block' } } }), _jsx(Typography, { variant: "caption", sx: { color: '#244c2f', fontWeight: 800, letterSpacing: '0.36em' }, children: "WEST VIRGINIA" }), _jsx(Box, { sx: { height: 4, width: 64, bgcolor: WV_GOLD, borderRadius: 1, display: { xs: 'none', md: 'block' } } })] }), _jsx(Typography, { component: "div", sx: { fontSize: { xs: '2.2rem', md: '4.25rem' }, fontWeight: 800, color: '#0b2b4a', lineHeight: 1.02, fontFamily: theme.typography.fontFamily }, children: title }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2, justifyContent: { xs: 'center', lg: 'flex-start' }, mt: 1 }, children: [_jsx(Box, { sx: { height: 2, flex: 1, bgcolor: '#244c2f', opacity: 0.25 } }), _jsx(Typography, { variant: "subtitle2", sx: { color: '#244c2f', fontWeight: 800, letterSpacing: '0.36em', px: 1 }, children: subheading }), _jsx(Box, { sx: { height: 2, flex: 1, bgcolor: '#244c2f', opacity: 0.25 } })] }), _jsx(Typography, { variant: "body2", sx: { mt: 1, color: '#0b2b4a', fontWeight: 600 }, children: description })] })] })) : titleImage ? (_jsx(Box, { component: "h1", sx: {
                                                                width: { lg: '100%' },
                                                                m: 0,
                                                                mb: { xs: 0.35, md: 0.5 },
                                                                lineHeight: 0,
                                                                boxSizing: 'border-box',
                                                                alignSelf: { xs: 'center', md: 'flex-start' },
                                                            }, children: _jsx(Box, { component: "img", src: resolvePublicUrl(titleImage) ?? titleImage, alt: titleImageAlt ?? title, sx: {
                                                                    display: 'block',
                                                                    maxWidth: '100%',
                                                                    width: ll.titleImage.maxWidth,
                                                                    height: 'auto',
                                                                    maxHeight: ll.titleImage.maxHeight,
                                                                    objectFit: 'contain',
                                                                    objectPosition: { xs: 'center', lg: 'left' },
                                                                    mx: { xs: 'auto', md: 0 },
                                                                    filter: 'drop-shadow(0 1px 0 rgba(255,255,255,0.85))',
                                                                    ...ll.titleImageWideSx,
                                                                } }) })) : (_jsx(Typography, { variant: "h1", component: "h1", sx: {
                                                                width: { lg: '100%' },
                                                                m: 0,
                                                                color: 'primary.dark',
                                                                mb: { xs: 0.35, md: 0.5 },
                                                                textShadow: '0 1px 0 rgba(255,255,255,0.8)',
                                                                boxSizing: 'border-box',
                                                            }, children: title })), _jsxs(Box, { sx: {
                                                                width: '100%',
                                                                maxWidth: ll.heroCopy.copyColumnMaxWidth,
                                                                pl: { xs: 0, sm: 0.75, md: 1.5, lg: 2.25 },
                                                                boxSizing: 'border-box',
                                                            }, children: [_jsx(Typography, { variant: "overline", component: "p", sx: {
                                                                        width: '100%',
                                                                        maxWidth: ll.heroCopy.copyColumnMaxWidth,
                                                                        m: 0,
                                                                        mt: { xs: 0.15, md: 0.25 },
                                                                        alignSelf: 'stretch',
                                                                        color: showOneStop ? autoReadableColor : 'primary.dark',
                                                                        boxSizing: 'border-box',
                                                                        ...ll.heroCopy.subheading,
                                                                        fontWeight: 800,
                                                                    }, children: subheading }), description ? (_jsx(Typography, { variant: "body2", component: "p", sx: {
                                                                        width: '100%',
                                                                        maxWidth: ll.heroCopy.copyColumnMaxWidth,
                                                                        m: 0,
                                                                        mt: { xs: 0.5, md: 0.65 },
                                                                        alignSelf: 'stretch',
                                                                        color: showOneStop ? autoReadableColor : 'primary.dark',
                                                                        opacity: 0.9,
                                                                        boxSizing: 'border-box',
                                                                        whiteSpace: 'pre-line',
                                                                        ...ll.heroCopy.description,
                                                                        fontWeight: 600,
                                                                        '@media (max-width: 899.95px)': {
                                                                            display: '-webkit-box',
                                                                            WebkitLineClamp: 4,
                                                                            WebkitBoxOrient: 'vertical',
                                                                            overflow: 'hidden',
                                                                        },
                                                                    }, children: description })) : null] })] }) }), _jsx(LandingPortraitCarousel, { gridRows: landingAgencies, resolvedDepartments: resolvedDepartments, loading: agenciesLoading, loadError: agenciesLoadError, heading: agenciesHeading, carouselIntervalMs: agenciesCarouselIntervalMs, shareHeroBackdrop: agenciesShareHeroBackdrop, agenciesBackgroundResolved: agenciesBackgroundResolved, agenciesBgPosition: agenciesBgPosition, agenciesBgSize: agenciesBgSize })] }) })] })] }) }), _jsx(LandingBrowseBand, { tiles: resolvedDepartments, loading: departmentsLoading, loadError: departmentsLoadError, heading: departmentsHeading, subheading: departmentsSubheading, bandMarginBottom: agenciesMarginBottom, trustFeaturesPresent: false })] }));
};
export default LandingHero;
